import { browser } from '$app/environment';
import { stripGID } from '$lib/utils/functions/stripGID';

let insightQueue = []; // Queue the insights here until Services ready and then use as a polled batch.

const available = () => (browser ? window && window?.klaviyo : false);

const klaviyoInsights = [
    {
        type: 'gameStarted',
        transform: ['track', 'Game Started'],
        normalize: (insight) => insight
    },
    {
        type: 'gameLost',
        transform: ['track', 'Game Lost'],
        normalize: (insight) => insight
    },
    {
        type: 'gameWon',
        transform: ['track', 'Game Won'],
        normalize: (insight) => insight
    },
    {
        type: 'productView',
        transform: ['track', 'Viewed Product'],
        normalize: (insight) => {
            return {
                ProductName: insight?.productTitle,
                ProductID: insight?.productId,
                SKU: insight?.sku,
                Categories: insight?.productCollections,
                ImageURL: insight?.variantImage,
                ...(insight?.productHandle && window?.location?.origin ? { URL: `${window.location.origin}/products/${insight.productHandle}` } : {}),
                Brand: insight?.productVendor,
                Price: insight?.price,
                CompareAtPrice: insight?.compareAtPrice
            };
        }
    },
    {
        type: 'itemView',
        transform: ['trackViewedItem'],
        normalize: (insight) => {
            return {
                Title: insight?.productTitle,
                ItemId: insight?.productId,
                Categories: insight?.productCollections,
                ImageUrl: insight?.variantImage,
                ...(insight?.productHandle && window?.location?.origin ? { Url: `${window.location.origin}/products/${insight.productHandle}` } : {}),
                Metadata: {
                    Brand: insight?.productVendor,
                    Price: insight?.price,
                    CompareAtPrice: insight?.compareAtPrice
                }
            };
        }
    },
    {
        type: 'addToCart',
        transform: ['track', 'Added to Cart'],
        normalize: (insight) => {
            const Items =
                insight?.cart?.cart?.lines?.edges?.map((edge) => {
                    const ProductCategories = (() => {
                        const collections = [];
                        if (edge?.node?.merchandise?.product?.productType) collections.push(edge.node.merchandise.product.productType);
                        if (edge?.node?.merchandise?.product) {
                            const product = edge.node.merchandise.product;
                            if (product?.productSubType?.value) collections.push(product.productSubType.value);
                            if (product?.tags?.length) collections.push(...product.tags);
                        }
                        return Array.from(new Set(collections));
                    })();

                    return {
                        ProductID: edge?.node?.merchandise?.product?.id ? stripGID(edge.node.merchandise.product.id) : undefined,
                        SKU: edge?.node?.merchandise?.sku,
                        ProductName: edge?.node?.merchandise?.product?.title,
                        Quantity: edge?.node?.quantity,
                        ItemPrice: edge?.node?.merchandise?.price?.amount ? Number(edge.node.merchandise.price.amount) : undefined,
                        RowTotal: edge?.node?.cost?.totalAmount?.amount ? Number(edge.node.cost.totalAmount.amount) : undefined,
                        ...(edge?.node?.merchandise?.product?.handle && window?.location?.origin
                            ? { ProductURL: `${window.location.origin}/products/${edge.node.merchandise.product.handle}` }
                            : {}),
                        ImageURL: edge?.node?.merchandise?.image?.url,
                        ProductCategories
                    };
                }) || []; // Short circuit to an empty array if the chain is nullable

            return {
                $value: insight?.cart?.cart?.cost?.totalAmount?.amount,
                AddedItemProductName: insight?.productTitle, // TODO : Add Brand and/or Variant?
                AddedItemProductID: insight?.productId,
                AddedItemSKU: insight?.sku,
                AddedItemCategories: Array.from(
                    new Set(Items.reduce((accumulator, item) => (!item?.ProductCategories ? accumulator : [...accumulator, ...item.ProductCategories]), []))
                ), // IS ALL ITEMS CONCAT BASED OF EXAMPLE
                AddedItemImageURL: insight?.variantImage,
                ...(insight?.productHandle && window?.location?.origin ? { AddedItemURL: `${window.location.origin}/products/${insight.productHandle}` } : {}),
                AddedItemPrice: insight?.price,
                AddedItemQuantity: insight?.quantity,
                ItemNames: Array.from(new Set(Items.map((item) => item?.ProductName))), // Top level filterable list of items, dedupe or add brands?
                CheckoutURL: insight?.cart?.cart?.checkoutUrl, // TODO : Or our static checkout route?
                Items
            };
        }
    },
    { type: 'updateToCart', transform: ['track', 'Updated Cart'], normalize: (insight) => insight }
];

function emitBatch() {
    if (insightQueue.length) {
        insightQueue.forEach((insight) => {
            const forUs = klaviyoInsights.find((klaviyoInsight) => klaviyoInsight?.type === insight?.type);
            if (!forUs) return true;

            const data = [...forUs.transform, forUs.normalize({ ...insight.insightData, time: insight.ISODateTime })];
            window.klaviyo.push(data);
            insightQueue = insightQueue.filter((itm) => itm !== insight); // Remove from queue
            return true;
        });
    }
}

function add(insightsArray) {
    insightQueue.push(...insightsArray);

    if (!available()) {
        return false;
    }

    emitBatch();
    return true;
}

export default { available, add, emitBatch };
